import { useState, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';

interface UseFormSubmitOptions {
  onSuccess?: (data?: unknown) => void;
  onError?: (error: Error) => void;
  successMessage?: string;
  errorMessage?: string;
}

export function useFormSubmit<T extends (...args: unknown[]) => Promise<unknown>>(
  submitFn: T,
  options: UseFormSubmitOptions = {}
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const {
    onSuccess,
    onError,
    successMessage = 'Operation completed successfully',
    errorMessage = 'An error occurred',
  } = options;

  const handleSubmit = useCallback(
    async (...args: Parameters<T>) => {
      // Prevent double submission
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        const result = await submitFn(...args);

        if (successMessage) {
          toast({
            title: successMessage,
            variant: 'default',
          });
        }

        onSuccess?.(result);

        return result;
      } catch (error) {
        const err = error as Error;

        toast({
          title: errorMessage,
          description: err.message,
          variant: 'destructive',
        });

        onError?.(err);
        throw error;
      } finally {
        // Small delay to prevent UI flashing
        setTimeout(() => {
          setIsSubmitting(false);
        }, 300);
      }
    },
    [submitFn, isSubmitting, successMessage, errorMessage, onSuccess, onError, toast]
  );

  return {
    handleSubmit,
    isSubmitting,
  };
}
